/**
 * Utility functions for updating meta tags dynamically
 * This helps with social media sharing by setting correct og:url and other meta tags
 */

/**
 * Updates or creates a meta tag
 * @param {string} property - The property attribute (e.g., 'og:url', 'og:title')
 * @param {string} content - The content value for the meta tag
 */
const setMetaTag = (property, content) => {
  // Try to find existing meta tag by property
  let metaTag = document.querySelector(`meta[property="${property}"]`);

  // If not found by property, try by name
  if (!metaTag) {
    metaTag = document.querySelector(`meta[name="${property}"]`);
  }

  if (metaTag) {
    // Update existing meta tag
    metaTag.setAttribute("content", content);
  } else {
    // Create new meta tag
    metaTag = document.createElement("meta");
    metaTag.setAttribute("property", property);
    metaTag.setAttribute("content", content);
    document.head.appendChild(metaTag);
  }
};

/**
 * Updates all relevant meta tags for a page
 * @param {Object} options - Meta tag options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.url - Current page URL (optional, defaults to window.location.href)
 * @param {string} options.image - Image URL for sharing (optional)
 */
export const updateMetaTags = ({ title, description, url, image }) => {
  const currentUrl = url || window.location.href;

  // Update document title
  if (title) {
    document.title = title;
    setMetaTag("og:title", title);
    setMetaTag("twitter:title", title);
  }

  // Update description
  if (description) {
    setMetaTag("description", description);
    setMetaTag("og:description", description);
    setMetaTag("twitter:description", description);
  }

  // Always update URL to current page URL
  setMetaTag("og:url", currentUrl);
  setMetaTag("twitter:url", currentUrl);

  // Update image if provided
  if (image) {
    setMetaTag("og:image", image);
    setMetaTag("twitter:image", image);
  }

  // Set type
  setMetaTag("og:type", "website");
  setMetaTag("twitter:card", "summary");
};

/**
 * Resets meta tags to default values
 */
export const resetMetaTags = () => {
  updateMetaTags({
    title: "LocusRags",
    description:
      "LocusRags - Order management tool for managing and tracking orders efficiently",
  });
};

export default updateMetaTags;
